import {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Select,
  SelectProps,
} from '@mantine/core'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'

type LegacyItemComponent = React.ComponentType<
  ComboboxItem & Record<string, unknown>
>

export interface InputSelectProps
  extends Omit<
    SelectProps,
    'icon' | 'nothingFound' | 'itemComponent' | 'withinPortal' | 'zIndex'
  > {
  name: string
  icon?: IconType
  itemComponent?: LegacyItemComponent
  nothingFound?: React.ReactNode
  withinPortal?: boolean
  zIndex?: number
  dropdownPosition?: 'top' | 'bottom'
}

export const InputSelect = ({
  name,
  label,
  data,
  icon: Icon,
  required,
  disabled,
  itemComponent: ItemComponent,
  nothingFound,
  renderOption,
  comboboxProps,
  withinPortal,
  zIndex,
  dropdownPosition,
  ...props
}: InputSelectProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()

  const items = data || []
  const legacyRenderOption = ItemComponent
    ? (item: ComboboxLikeRenderOptionInput<ComboboxItem>) => (
        <ItemComponent {...item.option} />
      )
    : undefined

  if (data) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field: { ref, value, onChange, ...field }, fieldState }) => {
          const { error } = fieldState
          const selectValue =
            value === null || value === undefined ? null : String(value)

          return (
            <Select
              ref={ref}
              label={label}
              leftSection={
                Icon ? <Icon color="currentColor" size={18} /> : null
              }
              data={items}
              error={error?.message}
              disabled={disabled}
              required={required}
              searchable
              nothingFoundMessage={nothingFound ?? t('generic.noResults')}
              renderOption={renderOption ?? legacyRenderOption}
              comboboxProps={{
                withinPortal,
                zIndex,
                position: dropdownPosition,
                ...comboboxProps,
              }}
              {...field}
              {...props}
              value={selectValue}
              onChange={onChange}
            />
          )
        }}
      />
    )
  }
  return null
}
